import { useAuth } from '../composables/auth/useAuth.js'
import {
  estRubriqueConciergerie,
  formateurHomePath,
  resolveFormateurRedirectPath,
} from '../utils/formateur-redirects.js'
import { isAdminOnlyVeilleType } from '../constants/veille.js'
import { hasAdminRights, isSuperAdmin, aLeRoleComplementaire } from '../constants/roles.js'
// Liste partagée avec la réaction à une session expirée : une page ouverte aux
// visiteurs ne doit renvoyer personne vers l'écran de connexion.
import { isPublicRoute, PORTAL_PUBLIC_ROUTES } from './public-routes.js'
// Règle unique du `?redirect=` : partagée avec les quatre écrans de connexion.
import { resolveRedirectInterne, versConnexion } from '../utils/redirect-interne.js'
import { versConnexionApresExpiration } from '../utils/login-redirect.js'

/**
 * Check portal authentication (formateur, entreprise, stagiaire)
 */
async function checkPortalAuth(portal, to, next) {
  const { authEndpoint, loginPath } = portal
  try {
    const resp = await fetch(authEndpoint, { credentials: 'include' })
    const data = await resp.json()
    if (data.id) {
      // Une session « en attente d'adhésion » (formateur venu de l'autre
      // réseau, passerelle jamais validée) ne donne accès à rien côté API :
      // chaque appel répondrait 403 ADHESION_REQUISE. Seule la page de
      // connexion héberge l'écran qui débloque — on l'y renvoie plutôt que
      // de la laisser entrer dans une rubrique inutilisable.
      if (data.adhesionRequise) {
        return next(loginPath)
      }
      // Détour propre au portail : une session qui n'a droit qu'à une partie de
      // l'espace est renvoyée vers elle plutôt que laissée sur une page dont
      // chaque appel serait refusé (cf. les comptes de conciergerie).
      const detour = portal.detour?.(data, to)
      if (detour && detour !== to.path) return next(detour)
      return next()
    }
  } catch {}
  // Sans session, la page demandée part en `?redirect=` (frère jumeau de la
  // garde du back-office) : un lien profond ouvert depuis un favori ou un
  // e-mail — `/espace-formateur/avis`, `/avis` — était jusqu'ici repris par la
  // réaction au 401 des préférences du back-office. Ce 401 n'étant plus pris
  // pour une expiration, c'est ici que la page doit être déposée, sans quoi
  // l'écran de connexion du portail ramenait au tableau de bord.
  return next(versConnexion(loginPath, to.fullPath))
}

/**
 * Redirect away from portal login pages if already authenticated
 */
async function redirectIfPortalAuthenticated(apiEndpoint, resolvePath, next) {
  try {
    const resp = await fetch(apiEndpoint, { credentials: 'include' })
    const data = await resp.json()
    // Une session « en attente d'adhésion » reste sur la page de connexion :
    // c'est la seule à héberger l'écran de passerelle qui la débloque.
    if (data && data.id && !data.adhesionRequise) {
      return next(resolvePath(data))
    }
  } catch {}
  return next()
}

function resolveFormateurRedirect(query) {
  return resolveFormateurRedirectPath(query?.redirect)
}

/**
 * Où envoyer quelqu'un qui arrive sur la connexion d'un portail alors qu'il a
 * déjà une session : la page qu'il demandait, sinon l'accueil de son espace.
 *
 * Une ACTION connue (clé de `FORMATEUR_REDIRECTS`) d'abord, puis une PAGE de ce
 * portail — celle que la détection de session expirée a déposée en
 * `?redirect=`. Sans ce second temps, la page quittée était perdue en silence.
 */
function resolvePortalTarget(portal, query, compte) {
  return portal.resolveRedirect?.(query)
    || resolveRedirectInterne(query?.redirect, {
      defaut: portal.homePath(compte),
      prefixe: portal.prefix.replace(/\/$/, ''),
      loginPath: portal.loginPath
    })
}

/**
 * Portal configurations (formateur, entreprise, stagiaire)
 */
const PORTAL_CONFIGS = [
  {
    prefix: '/espace-formateur/',
    loginPath: '/espace-formateur/login',
    // L'espace Alertis s'ouvre sur son tableau de bord : le travail confié par
    // l'équipe, l'agenda, le dossier à compléter et les CV par thème. (Le
    // réseautage entre formateurs, lui, a déménagé chez Global Prévention.)
    // Un compte ouvert par la conciergerie, lui, n'a que sa rubrique.
    homePath: formateurHomePath,
    authEndpoint: '/api/portail/auth/me',
    metaKey: 'requiresFormateurAuth',
    resolveRedirect: resolveFormateurRedirect,
    // Un compte de conciergerie qui tape l'adresse d'une autre rubrique est
    // ramené sur la sienne. Le serveur la refuserait de toute façon (403
    // CONCIERGERIE_SEULE) : sans ce détour, il resterait devant une page vide
    // sans comprendre pourquoi.
    detour: (formateur, to) =>
      formateur?.accesConciergerieUniquement && !estRubriqueConciergerie(to.path)
        ? formateurHomePath(formateur)
        : null,
    // Le laissez-passer de la passerelle inter-réseaux arrive dans cette
    // requête-là, et sur cette page-là. Seul le portail formateur en a un :
    // les portails entreprise et stagiaire ne déclarent pas cette clé et leur
    // garde reste exactement ce qu'il était (cf. `beforeEach`).
    passerelleQueryKey: 'jeton',
  },
  {
    prefix: '/espace-entreprise/',
    loginPath: '/espace-entreprise/login',
    homePath: () => '/espace-entreprise/dashboard',
    authEndpoint: '/api/entreprise/auth/me',
    metaKey: 'requiresEntrepriseAuth'
  },
  {
    prefix: '/espace-stagiaire/',
    loginPath: '/espace-stagiaire/login',
    homePath: () => '/espace-stagiaire/dashboard',
    authEndpoint: '/api/stagiaire/auth/me',
    metaKey: 'requiresStagiaireAuth'
  }
]

/**
 * Main navigation guard
 */
export function setupNavigationGuards(router) {
  router.beforeEach(async (to, from, next) => {
    const { checkAuth, isLoading, user } = useAuth()

    // For embed routes, skip auth check completely for better performance
    if (to.path.startsWith('/embed')) {
      isLoading.value = false
      return next()
    }

    // For portal routes (formateur, entreprise, stagiaire)
    for (const portal of PORTAL_CONFIGS) {
      if (to.path === portal.loginPath) {
        if (isLoading.value) await checkAuth()

        // UN LAISSEZ-PASSER PASSE AVANT LA SONDE DE SESSION.
        //
        // La session de portail est `rolling` sur trente jours et rien ne la
        // ferme sur inactivité : un formateur connecté la veille en a encore
        // une le lendemain. S'il ouvre le formulaire d'inscription (poste
        // partagé, ou seconde inscription pour l'autre réseau), prouve son mot
        // de passe et traverse la passerelle, `redirectIfPortalAuthenticated`
        // voyait cette vieille session et le renvoyait sur le tableau de bord
        // AVANT que l'écran de connexion n'ait pu consommer son jeton : il
        // atterrissait dans le compte déjà ouvert — pas celui dont il venait de
        // taper le mot de passe — et modifiait le profil de quelqu'un d'autre.
        //
        // On laisse donc entrer. Rien n'est affaibli : le POST
        // `/auth/passerelle` régénère la session et écrase la précédente, et un
        // jeton falsifié ou périmé est refusé par le serveur, qui laisse alors
        // la page de connexion ordinaire.
        if (portal.passerelleQueryKey && to.query[portal.passerelleQueryKey]) {
          return next()
        }

        // L'accueil dépend du COMPTE (un compte de conciergerie n'a que sa
        // rubrique) : la cible se résout donc une fois la session connue.
        const cibleSiConnecte = (compte) => resolvePortalTarget(portal, to.query, compte)
        return redirectIfPortalAuthenticated(portal.authEndpoint, cibleSiConnecte, next)
      }
      if (to.path.startsWith(portal.prefix)) {
        if (to.meta[portal.metaKey]) {
          // Admin preview: BO admins can access portal pages in read-only mode
          if (to.meta.allowAdminPreview && to.query.adminPreview === '1') {
            const adminUser = await checkAuth()
            if (adminUser && hasAdminRights(adminUser.role)) {
              return next()
            }
          }
          return checkPortalAuth(portal, to, next)
        }
        return next()
      }
    }

    // For other portal public routes (mission-reponse, questionnaire)
    if (PORTAL_PUBLIC_ROUTES.includes(to.path)) {
      if (isLoading.value) await checkAuth()
      return next()
    }

    // For other public routes, just ensure loading is done
    if (isPublicRoute(to.path)) {
      if (isLoading.value) {
        await checkAuth()
      }
      return next()
    }

    // For other routes, check auth (this will also set isLoading to false)
    // Un utilisateur affiché AVANT la sonde et plus de session APRÈS : c'est
    // une session qui vient de tomber, pas un visiteur arrivé par un favori.
    const avaitSession = !!user.value
    const authData = await checkAuth()

    if (to.meta.requiresAuth && !authData) {
      // LA PAGE DEMANDÉE PART EN `redirect`. Sans elle, une session tombée en
      // cours de route faisait perdre la page au premier clic sur un lien
      // interne : retour à l'accueil après reconnexion, sans un mot
      // d'explication — alors que le MÊME cas détecté par un 401
      // (`useSessionExpiryRedirect`) la reprend et l'annonce. Les deux chemins
      // mènent désormais au même écran de connexion, avec la même promesse.
      // (Sauf la racine : rien à reprendre, cf. `versConnexion`.)
      //
      // Le bandeau « Votre session s'est fermée » n'est promis que si une
      // session existait : un favori ouvert sans jamais s'être connecté garde
      // sa page, mais sans ce motif faux (cf. `versConnexionApresExpiration`).
      next(avaitSession
        ? versConnexionApresExpiration('/login', to.fullPath)
        : versConnexion('/login', to.fullPath))
    } else if (to.path === '/login' && authData) {
      // Déjà connecté sur `/login?redirect=…` : on ouvre la page demandée, pas
      // l'accueil. Même garde que l'écran de connexion lui-même (un chemin
      // interne, jamais une URL absolue).
      next(resolveRedirectInterne(to.query.redirect))
    } else if (to.meta.requiresSuperAdmin) {
      // Réservé au panneau d'activité commerciale : super_admin uniquement,
      // même un admin classique est renvoyé vers l'accueil.
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      })
      const data = await response.json()

      if (data.authenticated && isSuperAdmin(data.user.role)) {
        next()
      } else {
        next('/')
      }
    } else if (to.meta.requiresRoleComplementaire) {
      // Rôle complémentaire (ex. `pointeuse`) : le rôle principal ne compte pas,
      // un admin sans ce rôle est renvoyé vers l'accueil. Relu en base par
      // /api/auth/me, donc un rôle posé à la main vaut sans reconnexion.
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      })
      const data = await response.json()

      if (data.authenticated && aLeRoleComplementaire(data.user, to.meta.requiresRoleComplementaire)) {
        next()
      } else {
        next('/')
      }
    } else if (to.meta.requiresAdmin) {
      // Check if user has admin rights (admin ou super_admin)
      const response = await fetch('/api/auth/me', {
        credentials: 'include'
      })
      const data = await response.json()

      if (data.authenticated && hasAdminRights(data.user.role)) {
        next()
      } else {
        next('/')
      }
    } else if (to.path.startsWith('/veille/') && isAdminOnlyVeilleType(to.params.type) && !hasAdminRights(authData?.role)) {
      // Veille réservée aux admins (ex. /veille/rh) : on renvoie les non-admins
      // vers le hub plutôt que de leur ouvrir une page qu'ils ne doivent pas voir.
      next('/veille')
    } else {
      next()
    }
  })

  // Update page title on route change
  router.afterEach((to) => {
    const title = to.meta.title || 'Alertis - Back Office'
    document.title = title
  })
}
